import { useState } from "react";
import { Button, Form, Input, InputNumber, Modal, Table, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../components/PageHeader";
import { useApi } from "../../helpers/useApi";
import { useTaxPoliciesQuery } from "../../helpers/queries";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { TaxPolicy } from "../../api-models";

// Tax policies are a small reference list - /api/tax-policies returns a plain array rather
// than the Paginated<T> envelope ListPage expects, so this page uses the query hook
// directly (same pattern as UnitsPage).
export default function TaxPoliciesPage() {
  const { user } = useUser();
  const canWrite = hasPermission(user, "Items.Write");
  const { data: policies = [], isLoading } = useTaxPoliciesQuery();
  const { request } = useApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TaxPolicy | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  async function handleSubmit() {
    const values = await form.validateFields();
    if (editing) await request({ method: "PUT", url: `/tax-policies/${editing.id}`, data: values });
    else await request({ method: "POST", url: "/tax-policies", data: values });
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ["tax-policies"] });
    // A rate a material is assigned shows on the material master's own list.
    queryClient.invalidateQueries({ queryKey: ["items"] });
  }

  return (
    <>
      <PageHeader
        title="Tax policies"
        extra={
          canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                form.resetFields();
                setOpen(true);
              }}
            >
              New tax policy
            </Button>
          )
        }
      />
      <Typography.Paragraph type="secondary">
        A named rate a material can carry as its default. A purchase order line starts on its material's policy and
        the buyer can change or remove it; the tax the lines produce is added to the order's grand total. Editing a
        policy never reprices an order already raised - each line keeps the rate it was saved with.
      </Typography.Paragraph>
      <Table<TaxPolicy>
        rowKey="id"
        loading={isLoading}
        dataSource={policies}
        columns={[
          { title: "Name", dataIndex: "name" },
          { title: "Percentage", dataIndex: "percentage", render: (v: number) => `${v}%` },
          ...(canWrite
            ? [
                {
                  title: "",
                  key: "edit",
                  render: (_: unknown, r: TaxPolicy) => (
                    <Button
                      type="link"
                      onClick={() => {
                        setEditing(r);
                        form.setFieldsValue(r);
                        setOpen(true);
                      }}
                    >
                      Edit
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Modal
        title={editing ? `Edit ${editing.name}` : "New tax policy"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSubmit}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="GST 8%" />
          </Form.Item>
          <Form.Item
            name="percentage"
            label="Percentage"
            rules={[{ required: true }]}
            tooltip="Must be unique: the material master import matches a workbook's tax column on the number alone."
          >
            <InputNumber min={0} max={100} step={0.25} style={{ width: "100%" }} addonAfter="%" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
